import { useState, useEffect } from 'react'
import { Camera, Check, Sparkles } from 'lucide-react'
import { Button } from './ui/button'
import FeatureHeader from './SectionHeader'
import { supabase } from '../supabase'
import { useAuth } from '../hooks/useAuth'
import { getGuestProfile, GUEST_PROFILE_KEY } from './FirstTimeOverview'
import {
  COMMON_AVOIDS,
  readSensitivityTags,
  normaliseAvoidTags,
} from '../utils/profileOptions'

const SKIN_TONES = [
  { label: 'Fair', color: '#FDDBB4' },
  { label: 'Light', color: '#F5C89A' },
  { label: 'Medium', color: '#E0A870' },
  { label: 'Tan', color: '#C68642' },
  { label: 'Deep', color: '#8D5524' },
  { label: 'Rich', color: '#4A2912' },
]

const UNDERTONES = [
  { label: 'Cool', hint: 'Pinkish, bluish hues' },
  { label: 'Warm', hint: 'Golden, peachy hues' },
  { label: 'Neutral', hint: 'Mix of both' },
  { label: 'Olive', hint: 'Greenish undertones' },
]

const SKIN_TYPES = ['Normal', 'Oily', 'Dry', 'Combination', 'Sensitive']

// Son:mi doesn't target under-18s — keep the picker to adult ranges only.
const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55+']

const CONCERNS = [
  'Acne', 'Dark Spots', 'Dullness', 'Fine Lines', 'Redness',
  'Large Pores', 'Uneven Texture', 'Hyperpigmentation', 'Dehydration', 'Sensitivity'
]

const CLIMATES = ['Humid', 'Dry', 'Temperate', 'Cold', 'Tropical']
const WATER_TYPES = ['Soft Water', 'Hard Water', 'Not Sure']
const BUDGETS = [
  { label: 'Budget Friendly', sub: '$–$$' },
  { label: 'Mid-Range', sub: '$$–$$$' },
  { label: 'Luxury', sub: '$$$–$$$$' },
  { label: 'No Preference', sub: 'All Ranges' },
]

function SectionHeader({ label }) {
  return (
    <p className="text-[13px] font-semibold uppercase tracking-widest text-[#2A324B]/40 mb-3 mt-1">
      {label}
    </p>
  )
}

export function PersonalProfile({ onBack, onContinueToChat, onSave }) {
  const { getAuthSession, user } = useAuth()
  const [activeTab, setActiveTab] = useState('skin')
  const [userId, setUserId] = useState(null)

  const [userName, setUserName] = useState(user?.user_metadata?.first_name || '')
  const [editingName, setEditingName] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [profile, setProfile] = useState({
    skinTone: '',
    undertone: '',
    skinType: '',
    ageRange: '',
    concerns: [],
    // Things-to-avoid live in user_profiles.sensitivities (text[]). Pure
    // freetext tag input — type, press Enter, it becomes a removable pill.
    // Empty means un-answered (no explicit "nothing" state — blank IS that).
    sensitivityTags: [],
    sensitivityDraft: '',  // text currently in the input box (not yet a tag)
    sensitivityDropdownOpen: false,  // combobox dropdown visibility
    climate: [], // Multiple selection
    waterType: '',
    budget: '',
    location: '',
    notes: '',
  })
  const [saved, setSaved] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Fetch user ID
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const { userId: currentUserId } = await getAuthSession()
        setUserId(currentUserId)
      } catch (error) {
        console.error('Error fetching user ID:', error)
      }
    }
    fetchUserId()
  }, [getAuthSession])

  // Reload profile when component becomes visible (e.g., after onboarding)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userId) {
        console.log('👁️ Page visible, refreshing profile...')
        setRefreshTrigger(prev => prev + 1)
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [userId])

  // Load profile from Supabase
  useEffect(() => {
    if (!userId) return

    const loadProfile = async () => {
      try {
        setIsLoading(true)
        console.log('🔄 Loading profile for userId:', userId)
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .single()

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading profile:', error)
          return
        }

        // Merge guest profile from localStorage if DB row has no onboarding answers
        const guest = getGuestProfile();
        const dbAgeRange = data?.age_range || guest?.age_range || '';
        const dbClimate = Array.isArray(data?.climate) && data.climate.length > 0
          ? data.climate
          : Array.isArray(guest?.climate) ? guest.climate : [];

        if (data?.first_name) setUserName(data.first_name)
        console.log('✅ Profile loaded:', data)
        setProfile({
          skinTone: data?.skin_tone || '',
          undertone: data?.undertone || '',
          skinType: data?.skin_type || '',
          ageRange: dbAgeRange.replace(/\u2013/g, '-'),
          // primary_concerns is user-controlled (the "what do I want to focus
          // on" picker). We display only canonical entries the user has
          // selected — any non-canonical AI-noise from older analyses stays in
          // the DB but doesn't show here. Rich AI observations live in
          // attributes / preferences (jsonb) for the prompt context layer.
          concerns: Array.isArray(data?.primary_concerns)
            ? data.primary_concerns
                .map(c => CONCERNS.find(k => k.toLowerCase() === String(c).toLowerCase()))
                .filter(Boolean)
            : [],
          // Merge structured sensitivities (canonical column) with legacy
          // `allergies` text (old freetext column we're deprecating). Both feed
          // backend safety checks, so we union them in the UI and the next save
          // re-writes everything to `sensitivities` only.
          ...(() => {
            const legacyAllergies = typeof data?.allergies === 'string'
              ? data.allergies.split(/[,;]/).map(s => s.trim()).filter(Boolean)
              : [];
            const merged = [
              ...(Array.isArray(data?.sensitivities) ? data.sensitivities : []),
              ...legacyAllergies,
            ];
            return {
              sensitivityTags: readSensitivityTags(merged),
              sensitivityDraft: '',
            };
          })(),
          climate: dbClimate.map(c => {
            const match = CLIMATES.find(k => k.toLowerCase() === c.toLowerCase());
            return match || c;
          }),
          waterType: data?.water_type || '',
          budget: (() => {
            const raw = data?.budget_range || '';
            if (raw === 'Budget') return 'Budget Friendly';
            if (raw === 'Mixed') return 'No Preference';
            return raw;
          })(),
          // Some legacy rows stored an empty JSON object ('{}') in `location`
          // when the field went unset. Treat those as empty for the form.
          location: (() => {
            const raw = data?.location;
            if (!raw || typeof raw !== 'string') return '';
            const trimmed = raw.trim();
            if (trimmed === '{}' || trimmed === '[]') return '';
            return trimmed;
          })(),
          notes: data?.notes || '',
        })

        // If DB had no onboarding data but guest profile exists, save it to Supabase now
        if (guest && (!data?.age_range || !data?.climate) && userId) {
          supabase.from('user_profiles').upsert(
            {
              user_id: userId,
              age_range: guest.age_range || data?.age_range || null,
              climate: guest.climate?.length > 0 ? guest.climate : data?.climate || null,
              sensitivities: guest.sensitivities?.length > 0 ? guest.sensitivities : data?.sensitivities || null,
            },
            { onConflict: 'user_id' }
          ).then(() => {
            try { localStorage.removeItem(GUEST_PROFILE_KEY) } catch {}
          })
        }
      } catch (err) {
        console.error('Error loading profile:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadProfile()
  }, [userId, refreshTrigger])

  const toggleConcern = (c) => {
    setProfile(p => ({
      ...p,
      concerns: p.concerns.includes(c)
        ? p.concerns.filter(x => x !== c)
        : p.concerns.length < 5 ? [...p.concerns, c] : p.concerns,
    }))
  }

  // Add a string as a tag (deduped case-insensitively).
  const addSensitivityTag = (raw) => {
    const trimmed = (raw || '').trim().slice(0, 60)
    if (!trimmed) return
    setProfile(p => {
      const exists = p.sensitivityTags.some(t => t.toLowerCase() === trimmed.toLowerCase())
      return {
        ...p,
        sensitivityTags: exists ? p.sensitivityTags : [...p.sensitivityTags, trimmed],
        sensitivityDraft: '',
      }
    })
  }
  const removeSensitivityTag = (tag) => {
    setProfile(p => ({
      ...p,
      sensitivityTags: p.sensitivityTags.filter(t => t !== tag),
    }))
  }
  // Enter / comma submits the current draft as a tag. Backspace on an empty
  // input pops the last tag (familiar tag-input behaviour).
  const onSensitivityKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addSensitivityTag(profile.sensitivityDraft)
    } else if (e.key === 'Backspace' && !profile.sensitivityDraft && profile.sensitivityTags.length > 0) {
      e.preventDefault()
      const last = profile.sensitivityTags[profile.sensitivityTags.length - 1]
      removeSensitivityTag(last)
    }
  }

  const toggleClimate = (c) => {
    setProfile(p => ({
      ...p,
      climate: p.climate.includes(c)
        ? p.climate.filter(x => x !== c)
        : [...p.climate, c],
    }))
  }

  // "Answered" when at least one tag has been added. Empty = un-answered;
  // the user can leave it blank if they don't have anything to flag.
  const sensitivitiesAnswered = profile.sensitivityTags.length > 0
  const completedFields = [
    profile.skinTone,
    profile.undertone,
    profile.skinType,
    profile.ageRange,
    profile.concerns.length > 0 ? 'x' : '',
    sensitivitiesAnswered ? 'x' : '',
    profile.climate.length > 0 ? 'x' : '',
    profile.waterType,
    profile.budget,
  ].filter(Boolean).length
  const totalFields = 9
  const pct = Math.round((completedFields / totalFields) * 100)

  const handleSave = async () => {
    if (!userId) return

    try {
      // Pull existing personalization_data so we don't blow away other keys.
      const { data: existing } = await supabase
        .from('user_profiles')
        .select('personalization_data')
        .eq('user_id', userId)
        .maybeSingle()
      const existingPD = (existing && typeof existing.personalization_data === 'object' && existing.personalization_data) || {}
      const existingUserSet = (existingPD.user_set && typeof existingPD.user_set === 'object') ? existingPD.user_set : {}

      // Mark canonical fields the user has explicitly set. The backend reads
      // this map and refuses to silently overwrite user-set values from AI
      // analysis. We only flag fields the user has provided a value for —
      // never unlock by saving a blank.
      const now = new Date().toISOString()
      const userSet = { ...existingUserSet }
      const markField = (key, value) => {
        const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0)
        if (isEmpty) {
          // User explicitly cleared this field — unlock so AI can repopulate.
          delete userSet[key]
        } else {
          userSet[key] = { set_by: 'user', at: now }
        }
      }
      markField('skin_type', profile.skinType)
      markField('skin_tone', profile.skinTone)
      markField('undertone', profile.undertone)
      markField('age_range', profile.ageRange)
      markField('budget_range', profile.budget)

      // Re-stitch sensitivities into the canonical text[] shape. Fold in any
      // uncommitted draft so users who tap Save mid-typing don't lose it.
      // Empty array → save NULL (un-answered).
      const pendingDraft = (profile.sensitivityDraft || '').trim()
      const sensitivitiesArr = pendingDraft
        ? normaliseAvoidTags([...profile.sensitivityTags, pendingDraft])
        : profile.sensitivityTags
      markField('sensitivities', sensitivitiesArr)

      const dataToSave = {
        user_id: userId,
        skin_tone: profile.skinTone || null,
        undertone: profile.undertone || null,
        skin_type: profile.skinType || null,
        age_range: profile.ageRange || null,
        primary_concerns: profile.concerns.length > 0 ? profile.concerns : null,
        sensitivities: sensitivitiesArr.length > 0 ? sensitivitiesArr : null,
        // Clear the legacy `allergies` freetext column on save — we just
        // migrated its content into `sensitivities` on load, so writing it
        // back would duplicate signals.
        allergies: null,
        climate: profile.climate.length > 0 ? profile.climate : null,
        water_type: profile.waterType || null,
        budget_range: profile.budget || null,
        location: (() => {
          // Normalise junk values ('{}', '[]', empty) to NULL so they don't
          // round-trip into the input and render as "{}".
          const trimmed = (profile.location || '').trim();
          if (!trimmed || trimmed === '{}' || trimmed === '[]') return null;
          return trimmed;
        })(),
        notes: profile.notes || null,
        personalization_data: { ...existingPD, user_set: userSet },
        updated_at: new Date().toISOString()
      }

      console.log('💾 Saving profile data:', dataToSave)

      const { data, error } = await supabase
        .from('user_profiles')
        .upsert(dataToSave, {
          onConflict: 'user_id'
        })
        .select()

      if (error) {
        console.error('❌ Supabase error details:', error)
        throw error
      }

      console.log('✅ Profile saved successfully:', data)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      onSave?.({ concerns: profile.concerns, completeness: pct })
    } catch (err) {
      console.error('❌ Full error object:', err)
      console.error('❌ Error message:', err.message)
      console.error('❌ Error details:', err.details)
      alert(`Error saving profile: ${err.message || 'Please try again.'}`)
    }
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F2EAE1]/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#F3D5B5] border-t-[#341C1C] mx-auto mb-3"></div>
          <p className="text-sm text-[#2A324B]/50">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-[#F2EAE1]/30">

      {/* ── Header ── */}
      <div className="bg-white border-b border-[#341C1C]/8 flex-shrink-0">
        <div className="px-4 pt-3 pb-2">
          <FeatureHeader title="My Profile" onBack={onBack} />
        </div>

        {/* Avatar + name row */}
        <div className="flex items-center gap-4 px-5 pb-5">
          <div className="relative">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#F6ECE0,#ECDCC8)', border: '1px solid #EAE0D4' }}
            >
              <span style={{ fontFamily: '"DM Sans",system-ui,sans-serif', fontWeight: 500, fontSize: 32, color: '#A8742F' }}>
                {userName ? userName[0].toUpperCase() : 'S'}
              </span>
            </div>
            <button className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#341C1C] rounded-full flex items-center justify-center shadow">
              <Camera className="h-3 w-3 text-[#F3D5B5]" />
            </button>
          </div>
          <div className="flex-1">
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={async e => {
                    if (e.key === 'Enter') {
                      const trimmed = nameInput.trim();
                      if (trimmed && userId) {
                        await supabase.from('user_profiles').upsert({ user_id: userId, first_name: trimmed }, { onConflict: 'user_id' });
                        setUserName(trimmed);
                      }
                      setEditingName(false);
                    }
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  onBlur={async () => {
                    const trimmed = nameInput.trim();
                    if (trimmed && userId) {
                      await supabase.from('user_profiles').upsert({ user_id: userId, first_name: trimmed }, { onConflict: 'user_id' });
                      setUserName(trimmed);
                    }
                    setEditingName(false);
                  }}
                  className="text-sm font-semibold text-[#341C1C] border-b border-[#341C1C]/30 bg-transparent focus:outline-none w-full"
                  placeholder="Your first name"
                />
              </div>
            ) : (
              <button
                onClick={() => { setNameInput(userName); setEditingName(true); }}
                className="flex items-center gap-1.5 group"
              >
                <p className="font-semibold text-[#341C1C]">{userName || 'Add your name'}</p>
                <svg className="h-3 w-3 text-[#341C1C]/30 group-hover:text-[#341C1C]/60 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
            <p className="text-xs text-[#2A324B]/50 mt-0.5">Personalise your skin journey</p>

            {/* Inline progress */}
            <div className="flex items-center gap-2 mt-2.5">
              <div className="flex-1 h-1 bg-[#F2EAE1] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#F3D5B5] to-[#c9996a] rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[13px] font-semibold text-[#341C1C]/50 w-9 text-right">{pct}%</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-t border-[#341C1C]/8">
          {['skin', 'lifestyle'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium relative transition-colors ${
                activeTab === tab
                  ? 'text-[#341C1C]'
                  : 'text-[#2A324B]/40 hover:text-[#2A324B]/60'
              }`}
            >
              {tab === 'skin' ? 'Skin Profile' : 'Lifestyle'}
              {activeTab === tab && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-[#341C1C] rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto h-0">
        <div className="px-5 py-5 space-y-6">

          {activeTab === 'skin' ? (
            <>
              {/* Skin Analysis CTA — just a button. No decorative overlay
                  (the gradient was a tap-trap on iOS). */}
              <div className="rounded-2xl bg-[#341C1C] px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#F3D5B5]/15 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-[#F3D5B5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">Analyse Your Skin</p>
                  <p className="text-white/50 text-xs mt-0.5">Auto-fill your profile in seconds</p>
                </div>
                <button
                  onClick={() => {
                    if (onContinueToChat) {
                      onContinueToChat({ startSkinAnalysis: true });
                    } else {
                      onBack();
                    }
                  }}
                  className="flex-shrink-0 bg-[#F3D5B5] text-[#341C1C] text-xs font-semibold px-4 py-2 rounded-xl hover:bg-[#e5c9a8] transition-colors"
                >
                  Start
                </button>
              </div>

              {/* Skin Tone */}
              <div>
                <SectionHeader label="Skin Tone" />
                <div className="flex gap-2.5 flex-wrap">
                  {SKIN_TONES.map(({ label, color }) => (
                    <button
                      key={label}
                      onClick={() => setProfile(p => ({ ...p, skinTone: label }))}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <div
                        className={`w-10 h-10 rounded-full transition-all ${
                          profile.skinTone === label
                            ? 'ring-2 ring-offset-2 ring-[#341C1C] scale-110'
                            : 'ring-1 ring-black/10 group-hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                      <span className={`text-[13px] font-medium transition-colors ${
                        profile.skinTone === label ? 'text-[#341C1C]' : 'text-[#2A324B]/50'
                      }`}>
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Undertone */}
              <div>
                <SectionHeader label="Undertone" />
                <div className="grid grid-cols-2 gap-2">
                  {UNDERTONES.map(({ label, hint }) => (
                    <button
                      key={label}
                      onClick={() => setProfile(p => ({ ...p, undertone: label }))}
                      className={`text-left px-4 py-3 rounded-xl border transition-all ${
                        profile.undertone === label
                          ? 'border-[#341C1C] bg-[#341C1C]/5'
                          : 'border-[#341C1C]/12 bg-white hover:border-[#341C1C]/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-sm font-medium ${
                          profile.undertone === label ? 'text-[#341C1C]' : 'text-[#2A324B]'
                        }`}>{label}</span>
                        {profile.undertone === label && (
                          <Check className="h-3.5 w-3.5 text-[#341C1C]" />
                        )}
                      </div>
                      <p className="text-[13px] text-[#2A324B]/40">{hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Skin Type */}
              <div>
                <SectionHeader label="Skin Type" />
                <div className="flex flex-wrap gap-2">
                  {SKIN_TYPES.map(type => (
                    <button
                      key={type}
                      onClick={() => setProfile(p => ({ ...p, skinType: type }))}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                        profile.skinType === type
                          ? 'bg-[#341C1C] text-[#F3D5B5] border-[#341C1C]'
                          : 'bg-white text-[#2A324B] border-[#341C1C]/15 hover:border-[#341C1C]/40'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Range */}
              <div>
                <SectionHeader label="Age Range" />
                <div className="flex flex-wrap gap-2">
                  {AGE_RANGES.map(range => (
                    <button
                      key={range}
                      onClick={() => setProfile(p => ({ ...p, ageRange: range }))}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                        profile.ageRange === range
                          ? 'bg-[#341C1C] text-[#F3D5B5] border-[#341C1C]'
                          : 'bg-white text-[#2A324B] border-[#341C1C]/15 hover:border-[#341C1C]/40'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* Concerns — multi-select, max 5 (focus-list semantic).
                  This is a user-controlled focus dial, not a mirror of every
                  observation we've made about them. Rich AI observations live
                  in user_profiles.attributes / preferences (jsonb) and feed
                  prompt context separately — they don't surface here. */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <SectionHeader label="Skin Concerns" />
                  <span className="text-[13px] text-[#2A324B]/40 -mt-3">
                    {profile.concerns.length}/5 selected
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {CONCERNS.map(concern => {
                    const selected = profile.concerns.includes(concern)
                    return (
                      <button
                        key={concern}
                        type="button"
                        onClick={() => toggleConcern(concern)}
                        className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                          selected
                            ? 'bg-[#341C1C] text-[#F3D5B5] border-[#341C1C]'
                            : 'bg-white text-[#2A324B] border-[#341C1C]/15 hover:border-[#341C1C]/40'
                        }`}
                      >
                        {concern}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Ingredients to avoid — combobox: scrollable suggestions from
                  COMMON_AVOIDS, filtered as the user types, with "Add
                  '<typed>'" appearing whenever the input doesn't exactly match
                  a suggestion so freetext entries are first-class. */}
              <div>
                <SectionHeader label="Ingredients to avoid" />
                <p className="text-xs text-[#2A324B]/55 -mt-2 mb-3">
                  Allergies, sensitivities, or anything you avoid.
                </p>

                {/* Existing tags row — same pill size as the other selection
                    bubbles in this section. Each tag has a × to remove. */}
                {profile.sensitivityTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {profile.sensitivityTags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-2 pl-4 pr-2 py-2 rounded-full border border-[#341C1C] bg-[#341C1C] text-[#F3D5B5] text-sm font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeSensitivityTag(tag)}
                          className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-[#F3D5B5]/15 transition-colors"
                          aria-label={`Remove ${tag}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <input
                    type="text"
                    value={profile.sensitivityDraft}
                    onChange={e => setProfile(p => ({ ...p, sensitivityDraft: e.target.value, sensitivityDropdownOpen: true }))}
                    onFocus={() => setProfile(p => ({ ...p, sensitivityDropdownOpen: true }))}
                    onKeyDown={onSensitivityKeyDown}
                    onBlur={() => {
                      // Delay so a click on a dropdown option fires before
                      // we close. Auto-commit any uncommitted typed text on
                      // close so it isn't lost.
                      setTimeout(() => {
                        const draft = (profile.sensitivityDraft || '').trim()
                        if (draft) addSensitivityTag(draft)
                        setProfile(p => ({ ...p, sensitivityDropdownOpen: false }))
                      }, 180)
                    }}
                    placeholder="Search or type your own…"
                    className="w-full h-11 px-4 rounded-xl border border-[#341C1C]/15 bg-white text-sm text-[#2A324B] placeholder:text-[#2A324B]/30 focus:outline-none focus:border-[#341C1C]/40 focus:ring-1 focus:ring-[#F3D5B5] transition-all"
                  />
                  {/* Dropdown caret on the right when closed; clears when typing */}
                  {!profile.sensitivityDropdownOpen && (
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2A324B]/40 pointer-events-none" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  )}

                  {profile.sensitivityDropdownOpen && (() => {
                    const query = (profile.sensitivityDraft || '').trim().toLowerCase()
                    const already = new Set(profile.sensitivityTags.map(t => t.toLowerCase()))
                    const suggestions = COMMON_AVOIDS.filter(opt =>
                      !already.has(opt.toLowerCase()) &&
                      (!query || opt.toLowerCase().includes(query))
                    )
                    const exactMatch = suggestions.some(s => s.toLowerCase() === query)
                    const showAddCustom = query && !exactMatch && !already.has(query)
                    if (suggestions.length === 0 && !showAddCustom) return null
                    return (
                      <div className="absolute top-full left-0 right-0 mt-1 z-20 max-h-64 overflow-y-auto rounded-xl border border-[#341C1C]/15 bg-white shadow-lg">
                        {suggestions.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            // onMouseDown (not onClick) so the input's blur
                            // doesn't fire before this handler does. Otherwise
                            // the dropdown unmounts mid-click.
                            onMouseDown={e => { e.preventDefault(); addSensitivityTag(opt) }}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#2A324B] hover:bg-[#FDF8F4] transition-colors border-b border-[#F0F0F0] last:border-b-0"
                          >
                            {opt}
                          </button>
                        ))}
                        {showAddCustom && (
                          <button
                            type="button"
                            onMouseDown={e => { e.preventDefault(); addSensitivityTag(profile.sensitivityDraft) }}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#341C1C] hover:bg-[#FDF8F4] transition-colors font-medium flex items-center gap-2"
                          >
                            <span className="text-[#7A1F1F]">+</span>
                            Add "{profile.sensitivityDraft.trim()}"
                          </button>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Climate - Multi-select */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <SectionHeader label="Climate" />
                  {profile.climate.length > 0 && (
                    <span className="text-[13px] text-[#2A324B]/40 -mt-3">
                      {profile.climate.length} selected
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {CLIMATES.map(c => (
                    <button
                      key={c}
                      onClick={() => toggleClimate(c)}
                      className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                        profile.climate.includes(c)
                          ? 'bg-[#341C1C] text-[#F3D5B5] border-[#341C1C]'
                          : 'bg-white text-[#2A324B] border-[#341C1C]/15 hover:border-[#341C1C]/40'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Water Type */}
              <div>
                <SectionHeader label="Water Type" />
                <div className="grid grid-cols-3 gap-2">
                  {WATER_TYPES.map(w => (
                    <button
                      key={w}
                      onClick={() => setProfile(p => ({ ...p, waterType: w }))}
                      className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all text-center ${
                        profile.waterType === w
                          ? 'border-[#341C1C] bg-[#341C1C]/5 text-[#341C1C]'
                          : 'border-[#341C1C]/15 bg-white text-[#2A324B] hover:border-[#341C1C]/40'
                      }`}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <SectionHeader label="Budget Preference" />
                <div className="grid grid-cols-2 gap-2">
                  {BUDGETS.map(({ label, sub }) => (
                    <button
                      key={label}
                      onClick={() => setProfile(p => ({ ...p, budget: label }))}
                      className={`px-4 py-3 rounded-xl border text-left transition-all ${
                        profile.budget === label
                          ? 'border-[#341C1C] bg-[#341C1C]/5'
                          : 'border-[#341C1C]/12 bg-white hover:border-[#341C1C]/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${
                          profile.budget === label ? 'text-[#341C1C]' : 'text-[#2A324B]'
                        }`}>{label}</span>
                        {profile.budget === label && (
                          <Check className="h-3.5 w-3.5 text-[#341C1C]" />
                        )}
                      </div>
                      <p className="text-[13px] text-[#2A324B]/40 mt-0.5">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div>
                <SectionHeader label="Location (Optional)" />
                <input
                  value={profile.location}
                  onChange={e => setProfile(p => ({ ...p, location: e.target.value }))}
                  placeholder="e.g. London, Tokyo…"
                  className="w-full h-11 px-4 rounded-xl border border-[#341C1C]/12 bg-white text-sm text-[#2A324B] placeholder:text-[#2A324B]/30 focus:outline-none focus:border-[#341C1C]/40 focus:ring-1 focus:ring-[#F3D5B5] transition-all"
                />
              </div>

              {/* Notes — preferences and lifestyle context. Sensitivities
                  moved to their own field on the Skin tab. */}
              <div>
                <SectionHeader label="Additional Notes (Optional)" />
                <textarea
                  value={profile.notes}
                  onChange={e => setProfile(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Preferences, lifestyle, anything else worth mentioning…"
                  className="w-full px-4 py-3 rounded-xl border border-[#341C1C]/12 bg-white text-sm text-[#2A324B] placeholder:text-[#2A324B]/30 focus:outline-none focus:border-[#341C1C]/40 focus:ring-1 focus:ring-[#F3D5B5] transition-all resize-none min-h-24"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Save ── */}
      <div className="px-5 py-4 flex-shrink-0 bg-white border-t border-[#341C1C]/8">
        <button
          onClick={handleSave}
          className={`w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
            saved
              ? 'bg-[#341C1C] text-[#F3D5B5]'
              : 'bg-[#F3D5B5] text-[#341C1C] hover:bg-[#e5c9a8] active:scale-[0.98]'
          }`}
        >
          {saved ? (
            <>
              <Check className="h-4 w-4" />
              Saved
            </>
          ) : (
            'Save Profile'
          )}
        </button>
      </div>
    </div>
  )
}
